import React from "react";

// ─── Field reference table ────────────────────────────────────────────────────

interface FieldDef {
  field: string;
  type: string;
  description: string;
}

interface ScopeDef {
  scope: string;
  fields: FieldDef[];
}

const SCOPES: ScopeDef[] = [
  {
    scope: "MerchantInfo",
    fields: [
      { field: "name",    type: "string",  description: "Store display name from Clover." },
      { field: "address", type: "string",  description: "Full street address of the merchant location." },
      { field: "phone",   type: "string",  description: "Merchant contact phone number." },
      { field: "website", type: "string",  description: "Merchant website URL." }
    ]
  },
  {
    scope: "Item",
    fields: [
      { field: "id",           type: "string",  description: "Clover item ID." },
      { field: "name",         type: "string",  description: "Item display name." },
      { field: "price",        type: "number",  description: "Item price in cents." },
      { field: "description",  type: "string",  description: "Item description text." },
      { field: "available",    type: "boolean", description: "Whether the item is currently available." },
      { field: "categoryName", type: "string",  description: "Name of the category this item belongs to." }
    ]
  },
  {
    scope: "Order",
    fields: [
      { field: "id",        type: "string",  description: "Clover order ID." },
      { field: "state",     type: "string",  description: "Order state, e.g. open, locked, paid." },
      { field: "total",     type: "number",  description: "Order total in cents." },
      { field: "createdAt", type: "string",  description: "ISO 8601 timestamp when the order was created." }
    ]
  },
  {
    scope: "Customer",
    fields: [
      { field: "id",          type: "string", description: "Clover customer ID." },
      { field: "firstName",   type: "string", description: "Customer first name." },
      { field: "lastName",    type: "string", description: "Customer last name." },
      { field: "email",       type: "string", description: "Customer email address." },
      { field: "phoneNumber", type: "string", description: "Customer phone number." }
    ]
  }
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  teamId: number;
  selectedConfigId: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function CloverDataMapping(_props: Props) {
  return (
    <div>
      <p className="text-sm text-zinc-500 mb-1">
        Reference Clover data in workflow variables using the template{" "}
        <code className="bg-zinc-100 px-1 py-0.5 rounded text-xs font-mono text-zinc-700">
          {"{clover.{scope}.{field}}"}
        </code>
        .
      </p>
      <p className="text-xs text-zinc-400 mb-4">
        Example: <code className="bg-zinc-100 px-1 py-0.5 rounded text-xs font-mono">{"{clover.Order.state}"}</code> or{" "}
        <code className="bg-zinc-100 px-1 py-0.5 rounded text-xs font-mono">{"{clover.Item.name}"}</code>
      </p>

      <div className="space-y-6">
        {SCOPES.map((scope) => (
          <div key={scope.scope}>
            <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">{scope.scope}</div>
            <div className="border border-zinc-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50 text-zinc-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium w-1/4">Field</th>
                    <th className="px-3 py-2 text-left font-medium w-1/6">Type</th>
                    <th className="px-3 py-2 text-left font-medium">Description</th>
                    <th className="px-3 py-2 text-left font-medium w-1/3">Variable</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {scope.fields.map((f) => (
                    <tr key={f.field} className="hover:bg-zinc-50">
                      <td className="px-3 py-2 font-mono text-zinc-700">{f.field}</td>
                      <td className="px-3 py-2 text-zinc-400">{f.type}</td>
                      <td className="px-3 py-2 text-zinc-500">{f.description}</td>
                      <td className="px-3 py-2">
                        <code className="bg-zinc-100 px-1.5 py-0.5 rounded font-mono text-[10px] text-zinc-600">
                          {`{clover.${scope.scope}.${f.field}}`}
                        </code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
